import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import useCurrency from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { apiMessage, DELIVERY_RETURN_DISPOSITIONS, DELIVERY_ROUTES, type Delivery } from '../deliveryApi';

/**
 * ADR-0029 §2.7 — the explicit human decision a FAILED delivery hands off:
 * what happens to the stock, and — optionally — the money. Never automatic:
 * `disposition` is always required, `refundAmount` is only sent when the
 * dispatcher actually names a payment to refund against, and the refund
 * itself is not invented here — it goes through the ordinary, unchanged
 * `PaymentsService.refund` gate on the backend.
 *
 * `paymentId` is a plain text field rather than a picker: this screen has no
 * reach into the order's own check/payment list (out of this epic's own
 * contract, and building a payment picker is a Billing-module concern), so
 * the dispatcher enters the id from the check they are already looking at.
 * Named here rather than silently working around it.
 */
export function ResolveReturnDialog({
  delivery,
  onClose,
  onDone,
}: {
  delivery: Delivery;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { decimals, code } = useCurrency(delivery.branchId);
  const step = decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`;

  const [disposition, setDisposition] = useState<string>('');
  const [returnNote, setReturnNote] = useState('');
  const [wantsRefund, setWantsRefund] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refundIncomplete = wantsRefund && (!paymentId.trim() || !refundAmount.trim());

  const submit = async () => {
    if (!disposition) return;
    setSubmitting(true);
    try {
      await api.post(DELIVERY_ROUTES.resolveReturn(delivery.id), {
        disposition,
        returnNote: returnNote.trim() || undefined,
        paymentId: wantsRefund ? paymentId.trim() : undefined,
        refundAmount: wantsRefund ? refundAmount.trim() : undefined,
        refundReason: wantsRefund ? refundReason.trim() || undefined : undefined,
      });
      toast.success(t('delivery.board.returnResolved'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.board.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('delivery.board.resolveReturn')}
      description={t('delivery.board.resolveReturnHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting || !disposition || refundIncomplete}>
            {t('delivery.board.confirmResolve')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('delivery.board.disposition')} required>
          <Select value={disposition} onChange={(e) => setDisposition(e.target.value)}>
            <option value="">{t('delivery.board.dispositionPlaceholder')}</option>
            {DELIVERY_RETURN_DISPOSITIONS.map((option) => (
              <option key={option} value={option}>
                {t(`delivery.returnDisposition.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('delivery.board.returnNote')}>
          <Textarea rows={2} value={returnNote} onChange={(e) => setReturnNote(e.target.value)} maxLength={1000} />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={wantsRefund}
            onChange={(e) => setWantsRefund(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong"
          />
          {t('delivery.board.wantsRefund')}
        </label>

        {wantsRefund && (
          <div className="space-y-3 rounded-lg border p-3">
            <Field label={t('delivery.board.paymentId')} required hint={t('delivery.board.paymentIdHint')}>
              <Input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} />
            </Field>
            <Field label={t('delivery.board.refundAmount', { currency: code })} required>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={step}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
            </Field>
            <Field label={t('delivery.board.refundReason')}>
              <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} maxLength={500} />
            </Field>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default ResolveReturnDialog;
